import { axios } from "..";
import { IPayment } from "../interfaces/models/IPayment";
import { STAFF_OVERRIDE_STATUS } from "../Enums/RegistrationEnum";

export const PAID_STATUSES_FOR_AMOUNT = ["completed", "partial", "paidSPK", "paidSPR"] as const;

export const isPaidStatusForAmount = (status: string): boolean =>
  PAID_STATUSES_FOR_AMOUNT.includes(status as any);

export const getAmountPaidFromPayments = (payments: IPayment[]): number => {
  if (!payments?.length) {
    return 0;
  }

  const safeAmount = (p: IPayment): number =>
    typeof p.amount === "number" && !Number.isNaN(p.amount) ? p.amount : 0;

  const staffOverrides = payments.filter((p) => p.status === STAFF_OVERRIDE_STATUS);

  if (staffOverrides.length > 0) {
    const latestStaff = staffOverrides.reduce((a, b) =>
      (b.paymentDate || 0) > (a.paymentDate || 0) ? b : a,
    );
    const cutoff = latestStaff.paymentDate || 0;
    const sumFuturePaid = payments
      .filter(
        (p) =>
          p !== latestStaff &&
          (p.paymentDate || 0) > cutoff &&
          isPaidStatusForAmount(String(p.status)),
      )
      .reduce((sum, p) => sum + safeAmount(p), 0);
    return safeAmount(latestStaff) + sumFuturePaid;
  }

  return payments
    .filter((p) => isPaidStatusForAmount(String(p.status)))
    .reduce((sum, p) => sum + safeAmount(p), 0);
};

export const getPaymentByTransactionId = async (
  transactionId: string
): Promise<IPayment | null> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        transactionId: {
          value: transactionId,
          comparisonType: "EQUAL",
          arraySearchField: "transactionId",
        },
      })
    );
    const response = await axios.Get(`./payment?${params.toString()}`);
    return response.data && response.data.length > 0 ? response.data[0] : null;
  } catch (error) {
    console.error("Error fetching payment by transaction ID:", error);
    return null;
  }
};

/** Latest payment of the ledger carrying the accumulated amount paid. */
export const buildRepresentativePayment = (
  payments: IPayment[]
): IPayment | null => {
  if (!payments?.length) {
    return null;
  }

  const amount = getAmountPaidFromPayments(payments);
  const representative = payments.reduce((a, b) =>
    (b.paymentDate || 0) > (a.paymentDate || 0) ? b : a
  );
  return { ...representative, amount };
};

export const getPaymentByRegistrationId = async (
  registrationId: string
): Promise<IPayment | null> => {
  try {
    const payments = await getAllPaymentsByRegistrationId(registrationId);
    return buildRepresentativePayment(payments);
  } catch (error) {
    console.error("Error fetching payment by registration ID:", error);
    return null;
  }
};

export const getAllPaymentsByRegistrationId = async (
  registrationId: string
): Promise<IPayment[]> => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        registrationId: { value: registrationId, comparisonType: "EQUAL" },
      })
    );
    const response = await axios.Get(`./payment?${params.toString()}`);
    
    if (response.data && Array.isArray(response.data)) {
      return response.data.filter(
        (payment: IPayment) => payment.registrationId === registrationId
      );
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching payments by registration ID:", error);
    return [];
  }
};

/**
 * Get all payments for multiple registration IDs
 * Returns a map of registrationId -> total amount paid (sum of all payments)
 */
export const getPaymentsByRegistrationIds = async (
  registrationIds: string[]
): Promise<Map<string, number>> => {
  const amountPaidMap = new Map<string, number>();
  
  if (!registrationIds || registrationIds.length === 0) {
    return amountPaidMap;
  }

  try {
    // Fetch payments for all registration IDs
    // Using IN comparison if supported, otherwise fetch individually
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        registrationId: { 
          value: registrationIds, 
          comparisonType: "IN" 
        },
      })
    );
    
    const response = await axios.Get(`./payment?${params.toString()}`);
    
    if (response.data && Array.isArray(response.data)) {
      const paymentsByRegId = new Map<string, IPayment[]>();
      response.data.forEach((payment: IPayment) => {
        if (!payment.registrationId) {
          return;
        }
        const list = paymentsByRegId.get(payment.registrationId) || [];
        list.push(payment);
        paymentsByRegId.set(payment.registrationId, list);
      });
      paymentsByRegId.forEach((payments, regId) => {
        const amount = getAmountPaidFromPayments(payments);
        if (amount > 0) {
          amountPaidMap.set(regId, amount);
        }
      });
    }
  } catch (error) {
    console.warn("Batch payment fetch failed, falling back to individual fetches:", error);
    const batchSize = 10;
    for (let i = 0; i < registrationIds.length; i += batchSize) {
      const batch = registrationIds.slice(i, i + batchSize);
      const paymentPromises = batch.map(async (id) => {
        const payments = await getAllPaymentsByRegistrationId(id);
        const amount = getAmountPaidFromPayments(payments);
        return { registrationId: id, amount };
      });
      const results = await Promise.all(paymentPromises);
      results.forEach(({ registrationId, amount }) => {
        if (amount > 0) {
          amountPaidMap.set(registrationId, amount);
        }
      });
    }
  }
  
  return amountPaidMap;
};

export const createPayment = async (
  payment: IPayment
): Promise<any> => {
  const response = await axios.Post("./payment", payment);
  return response.data;
};

export const updatePayment = async (payment: IPayment): Promise<any> => {
  const response = await axios.PutDataIntoModel("./payment", payment);
  return response.data;
};

export interface GeneratePaymentLinkRequest {
  amount: number;
  currency: string;
  registrationId?: string;
  eventId: string;
  callbackUrl?: string;
  description?: string;
  customFields?: Array<{ id: string; value: string }>;
}

export const generatePaymentLink = async (
  request: GeneratePaymentLinkRequest
): Promise<string | null> => {
  try {
    const requestBody: any = {
      method: "hpp-encrypted",
      amount: request.amount,
      currency: request.currency,
      eventId: request.eventId,
    };

    if (request.registrationId) {
      requestBody.registrationId = request.registrationId;
    }

    if (request.callbackUrl) {
      requestBody.callbackUrl = request.callbackUrl;
    }

    if (request.description) {
      requestBody.description = request.description;
    }

    if (request.customFields && request.customFields.length > 0) {
      requestBody.customFields = request.customFields;
    }

    const response = await axios.Post("/paya/generate-payform-url", requestBody);

    const url = response.data?.hppUrl || response.data?.payFormUrl;
    if (response.data?.success && url) {
      return url;
    }
    return null;
  } catch (error) {
    console.error("Error generating payment link:", error);
    return null;
  }
};

